import { MappedStorableItem } from '@bringg/dashboard-sdk/dist/data-entity';
import { Run, Task } from '@bringg/types';
import { getRoot } from 'mobx-easy';
import { runInAction } from 'mobx';
import _ from 'lodash';
import { localLogger } from '@bringg-frontend/bringg-web-infra';

import type RootStore from 'bringg-web/stores/root-store';
import { tableDefinitionUpdater } from './table-definition-updater';
import { tasksGroupUpdater } from './tasks-group-updater';
import { runsGroupUpdater } from './runs-group-updater';
import { TableDefinition } from 'bringg-web/features/monitoring/shared/stores/groups/table-definition';
import { monitoringRootStore } from 'bringg-web/features/monitoring/shared/stores/monitoring-store';

type RealtimeCallbacks = {
	id: string;
	item: MappedStorableItem<Task> | MappedStorableItem<Run>;
	callback: (tableDefinition: TableDefinition) => void;
};

const LOG_PREFIX = 'realtime listener:';

function listen(updateIntervalMs?: number) {
	const tasksStore = getRoot<RootStore>().data.tasksStore;
	const runStore = getRoot<RootStore>().data.runStore;

	const callbacks: RealtimeCallbacks[] = [];
	const intervalData = { updateIntervalMs, timeoutId: undefined };
	const subscriptions = [];

	subscriptions.push(
		tasksStore.onCreate((mappedStorableItem: MappedStorableItem<Task>) => {
			scheduleEvent(intervalData, callbacks, mappedStorableItem, 'task', (tableDefinition: TableDefinition) => {
				localLogger.log(
					`${LOG_PREFIX} running task [${mappedStorableItem.current.id}] create`,
					mappedStorableItem
				);
				tableDefinitionUpdater.update(mappedStorableItem, tasksGroupUpdater, tasksStore, tableDefinition);
			});
		})
	);

	subscriptions.push(
		tasksStore.onUpdate((mappedStorableItem: MappedStorableItem<Task>) => {
			scheduleEvent(intervalData, callbacks, mappedStorableItem, 'task', (tableDefinition: TableDefinition) => {
				localLogger.log(
					`${LOG_PREFIX} running task [${mappedStorableItem.current.id}] update`,
					mappedStorableItem
				);
				tableDefinitionUpdater.update(mappedStorableItem, tasksGroupUpdater, tasksStore, tableDefinition);
			});
		})
	);

	subscriptions.push(
		tasksStore.onDelete((mappedStorableItem: MappedStorableItem<Task>) => {
			scheduleEvent(intervalData, callbacks, mappedStorableItem, 'task', (tableDefinition: TableDefinition) => {
				localLogger.log(
					`${LOG_PREFIX} running task [${mappedStorableItem.diff.id}] remove`,
					mappedStorableItem
				);
				tableDefinitionUpdater.remove(mappedStorableItem, tasksGroupUpdater, tasksStore, tableDefinition);
			});
		})
	);

	subscriptions.push(
		runStore.onCreate((mappedStorableItem: MappedStorableItem<Run>) => {
			scheduleEvent(intervalData, callbacks, mappedStorableItem, 'run', (tableDefinition: TableDefinition) => {
				localLogger.log(
					`${LOG_PREFIX} running run [${mappedStorableItem.current.id}] create`,
					mappedStorableItem
				);
				tableDefinitionUpdater.update(mappedStorableItem, runsGroupUpdater, runStore, tableDefinition);
			});
		})
	);

	subscriptions.push(
		runStore.onUpdate((mappedStorableItem: MappedStorableItem<Run>) => {
			scheduleEvent(intervalData, callbacks, mappedStorableItem, 'run', (tableDefinition: TableDefinition) => {
				localLogger.log(
					`${LOG_PREFIX} running run [${mappedStorableItem.current.id}] update`,
					mappedStorableItem
				);
				tableDefinitionUpdater.update(mappedStorableItem, runsGroupUpdater, runStore, tableDefinition);
			});
		})
	);

	subscriptions.push(
		runStore.onDelete((mappedStorableItem: MappedStorableItem<Run>) => {
			scheduleEvent(intervalData, callbacks, mappedStorableItem, 'run', (tableDefinition: TableDefinition) => {
				localLogger.log(`${LOG_PREFIX} running run [${mappedStorableItem.diff.id}] remove`, mappedStorableItem);
				tableDefinitionUpdater.remove(mappedStorableItem, runsGroupUpdater, runStore, tableDefinition);
			});
		})
	);

	return () => {
		intervalData.timeoutId && clearTimeout(intervalData.timeoutId);
		subscriptions.forEach(unsubscribe => unsubscribe());
	};
}

function scheduleEvent<T extends Run | Task>(
	intervalData: { timeoutId: number; updateIntervalMs: number },
	callbacks: RealtimeCallbacks[],
	item: MappedStorableItem<T>,
	type: 'task' | 'run',
	callback: (tableDefinition: TableDefinition) => void
) {
	const action = !item.current ? 'remove' : !item.diff ? 'create' : 'update';
	localLogger.log(`${LOG_PREFIX} got ${type} ${action}, scheduling refresh`, item);
	const tableDefinitionStore = monitoringRootStore.getStore().tableDefinitionStore;

	if (!intervalData.updateIntervalMs) {
		runInAction(() => {
			for (const tableDefinition of tableDefinitionStore.tableDefinitions.values()) {
				callback(tableDefinition);
			}
		});

		return;
	}

	const itemId = getCallbackItemId(type, item);
	const pendingCallback = callbacks.find(data => data.id === itemId);

	if (!pendingCallback || !item.current) {
		callbacks.push({ id: itemId, item, callback });
	} else {
		batchDiffs(pendingCallback.item, item);
	}

	if (!intervalData.timeoutId) {
		intervalData.timeoutId = window.setTimeout(() => {
			runInAction(() => {
				localLogger.log(
					`${LOG_PREFIX} ---------------------------running callbacks start-------------------------------`
				);

				for (const key of tableDefinitionStore.tableDefinitions.keys()) {
					localLogger.log(`${LOG_PREFIX} running for table definition with key ${key}`);

					callbacks.forEach(data => {
						data.callback(tableDefinitionStore.tableDefinitions.get(key));
					});
				}

				localLogger.log(
					`${LOG_PREFIX} ---------------------------running callbacks end---------------------------------`
				);
			});

			callbacks.length = 0;
			delete intervalData.timeoutId;
		}, intervalData.updateIntervalMs);
	}
}

function getCallbackItemId<T extends Run | Task>(type: string, item: MappedStorableItem<T>) {
	const itemId = (item.current || item.diff).id;
	return `${type}-${itemId}`;
}

function batchDiffs<T extends Run | Task>(existingItem: MappedStorableItem<T>, newItem: MappedStorableItem<T>) {
	existingItem.current = newItem.current;

	if (!existingItem.diff) {
		return;
	}

	addNewKeysToExistingDiff(existingItem.diff, newItem.diff);
	localLogger.log(
		`${LOG_PREFIX} got two updates of same entity ${existingItem.current.id}, batching updates into new diff: `,
		existingItem.diff
	);
}

function addNewKeysToExistingDiff<T>(existingDiff: T, newDiff: T) {
	for (const key of Object.keys(newDiff)) {
		if (!_.has(existingDiff, key)) {
			existingDiff[key] = newDiff[key];
		} else if (
			typeof newDiff[key] === 'object' &&
			newDiff[key] !== null &&
			typeof existingDiff[key] === 'object' &&
			existingDiff[key] !== null
		) {
			addNewKeysToExistingDiff(existingDiff[key], newDiff[key]);
		}
	}
}

export const tableDefinitionUpdatesListener = { listen };
